import { useEffect, useState } from 'react'
import { enqueueSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { getNotificationContainer } from '../container/notification-module'
import { NotificationService } from '../modules/notifications/services/NotificationService'
import { NOTIFICATION_SERVICE_KEY } from '../modules/notifications'
import { IGotifyMessageService } from '../modules/notifications/services/GotifyMessageService'
import { GOTIFY_SERVICE_KEY } from '../modules/notifications/container'
import { Notification } from '../modules/notifications/models/Notification'
import { adaptGotifyMessage } from '../modules/notifications/adapters/gotifyAdapter'
import { SendingStatus } from '../modules/notifications/enums/SendingStatus'
import { Query, QueryParam } from 'common/api/Query'

const notificationContainer = getNotificationContainer()
const notificationService = notificationContainer.get<NotificationService>(NOTIFICATION_SERVICE_KEY)

export const useNotifications = (token: string, userId: string) => {
  const { t } = useTranslation()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState<number>(0)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!token) {
      console.debug('No Gotify token available')
      return
    }

    if (!userId) return

    const container = getNotificationContainer()
    const gotifyWebSocketService = container.get<IGotifyMessageService>(GOTIFY_SERVICE_KEY)

    const subscription = gotifyWebSocketService.connect(token).subscribe({
      next: (connected) => {
        if (connected) {
          console.debug('Connected to Gotify WebSocket')
        } else {
          console.error('Failed to connect to Gotify')
          enqueueSnackbar(t('notificationsUnavailable'), { variant: 'error' })
        }
      },
      error: (error) => {
        console.error('Gotify connection error:', error)
        enqueueSnackbar(t('notificationsUnavailable'), { variant: 'error' })
      },
    })

    const messageSubscription = gotifyWebSocketService.getMessages().subscribe({
      next: (gotifyMessage) => {
        const notification = adaptGotifyMessage(gotifyMessage)
        if (notification.recipientID === userId) {
          setNotifications((prev) => [notification, ...prev])
          setUnreadCount((prev) => prev + 1)
        }
      },
      error: () => enqueueSnackbar(t('notificationsUnavailable'), { variant: 'error' }),
    })

    return () => {
      subscription.unsubscribe()
      messageSubscription.unsubscribe()
      gotifyWebSocketService.disconnect()
    }
  }, [token, t, userId])

  const loadNotifications = async (userId: string, newLimit?: number) => {
    if (!userId) return
    setLoading(true)

    const query = new Query({
      query: [new QueryParam('recipientID', userId)],
      sort: [{ field: 'createdAt', desc: true }],
    })

    try {
      const data = await notificationService.getFilteredItems(query).toPromise()
      setNotifications(data || [])
    } catch (err) {
      console.error('Error loading notifications:', err)
    } finally {
      setLoading(false)
    }
  }

  const markAsRead = async (notificationId: string) => {
    try {
      const currentNotification = notifications.find((n) => n.id === notificationId)
      if (!currentNotification) {
        enqueueSnackbar(t('notificationNotFound'), {
          variant: 'error',
          autoHideDuration: 3000,
        })
        throw new Error('Notification not found')
      }

      const updatedNotification = new Notification({
        id: currentNotification.id,
        title: currentNotification.title,
        message: currentNotification.message,
        recipientID: currentNotification.recipientID,
        senderID: currentNotification.senderID,
        createdAt: currentNotification.createdAt,
        deliveryStatus: SendingStatus.Read,
        eventType: currentNotification.eventType,
        ...(currentNotification as any),
      })

      await notificationService.update(updatedNotification).toPromise()

      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? updatedNotification : n))
      )

      setUnreadCount((prev) => Math.max(0, prev - 1))
    } catch (error) {
      console.error('Error marking notification as read:', error)
      enqueueSnackbar(t('errorMarkingNotificationAsRead'), { variant: 'error' })
    }
  }

  return {
    notifications,
    unreadCount,
    markAsRead,
    loading,
    loadNotifications,
    isLoading: false,
  }
}
